import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { contactMessageApi } from '../../api/contactMessageApi'
import { ApiError } from '../../api/httpClient'
import { MailIcon } from './icons'

/** Header/footer button that opens a small contact form; persisted via POST /api/contact-messages.
 *  Submittable anonymously or with an email address - if the sender happens to be logged in, the
 *  backend persists which customer/craftsman sent it based on the JWT, without any extra frontend logic. */
export function ContactFormButton() {
  const { t } = useTranslation()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function open() {
    setMessage('')
    setEmail('')
    setSubmitted(false)
    setError(null)
    dialogRef.current?.showModal()
  }

  function close() {
    dialogRef.current?.close()
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await contactMessageApi.create({ message, email: email.trim() === '' ? null : email.trim() })
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('contact.errorFallback'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <button type="button" className="contact-form-button" onClick={open} aria-label={t('contact.button')}>
        <MailIcon className="contact-form-icon" />
        <span className="btn-label">{t('contact.button')}</span>
      </button>
      <dialog ref={dialogRef} className="contact-form-dialog" aria-label={t('contact.title')}>
        {submitted ? (
          <div className="stack">
            <p>{t('contact.success')}</p>
            <button type="button" onClick={close}>
              {t('contact.close')}
            </button>
          </div>
        ) : (
          <form className="stack" onSubmit={handleSubmit}>
            <p className="dialog-title">{t('contact.title')}</p>
            <p className="muted">{t('contact.description')}</p>
            <label>
              {t('contact.textareaLabel')}
              <textarea
                rows={5}
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('contact.placeholder')}
              />
            </label>
            <label>
              {t('contact.emailLabel')}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('contact.emailPlaceholder')}
              />
            </label>
            {error && <p className="contact-form-error">{error}</p>}
            <div className="actions">
              <button type="button" className="btn-outline" onClick={close} disabled={submitting}>
                {t('contact.cancel')}
              </button>
              <button type="submit" disabled={submitting || message.trim() === ''}>
                {t('contact.submit')}
              </button>
            </div>
          </form>
        )}
      </dialog>
    </>
  )
}
